import type { CadArchiveFile, CadNode } from '@puppycad/proto'
import { CadArchiveFileSnapshotMapper } from './CadArchiveFileSnapshotMapper'
import type {
  CadNodeLoadSnapshot,
  NodeReloadApiAdapter,
  ReloadOpenProjectNodeInput,
} from './types/project-model-loading-types'

const UINT64_MAX = 18_446_744_073_709_551_615n

/**
 * GetCadNodeTree 请求在服务边界处需要的最小字段集合。
 *
 * 设计说明：proto 生成的 Message 类型会携带运行时元信息，但前端服务只需要向通信层交付
 * 纯请求数据；因此这里保留协议字段，不把调用方绑死到具体 protobuf 构造方式。
 */
export interface CadNodeTreeReloadRequest {
  /**
   * 目标节点仓库 ID。
   */
  readonly repositoryId: bigint
  /**
   * 目标分支 ID；0 表示由后端选择默认分支。
   */
  readonly branchId: bigint
  /**
   * 目标提交 ID；空字符串表示由后端选择分支 HEAD。
   */
  readonly commitId: string
}

/**
 * GetCadNodeTree 响应在服务边界处需要的最小字段集合。
 */
export interface CadNodeTreeReloadResponse {
  /**
   * 目标节点及其完整后代；缺失表示后端未能返回可加载节点。
   */
  readonly node?: CadNode
  /** assembly-single 格式的总归档包。 */
  readonly archiveFile?: CadArchiveFile
}

/**
 * 创建节点树重新加载适配器的依赖。
 */
export interface CreateCadNodeTreeReloadAdapterInput {
  /**
   * 读取指定仓库、分支和提交下完整 CAD 节点子树的通信函数。
   */
  readonly getCadNodeTree: (request: CadNodeTreeReloadRequest) => Promise<CadNodeTreeReloadResponse>
}

/**
 * 创建带定位上下文的节点重载参数错误。
 *
 * @param fieldName 出错字段名。
 * @param reason 字段无效原因。
 * @param nodeKey 当前重载目标节点 key。
 * @param rawValue 字段原始值。
 * @returns 带 nodeKey 和原始值的错误对象。
 */
function createReloadParameterError(fieldName: string, reason: string, nodeKey: string, rawValue: string): Error {
  return new Error(`节点重新加载参数无效：${fieldName} ${reason}，nodeKey=${nodeKey}，rawValue=${rawValue}`)
}

/**
 * 将字符串 ID 解析为 uint64。
 *
 * 设计说明：节点重载会直接命中后端版本树，必须在服务边界阻止空白、负数和越界值进入协议层。
 *
 * @param value 页面或模型树传入的 ID 原文。
 * @param fieldName 出错字段名。
 * @param nodeKey 当前重载目标节点 key。
 * @returns 可直接发送给后端协议的 uint64 bigint。
 */
function parseUint64Id(value: string, fieldName: string, nodeKey: string): bigint {
  const normalizedValue = value.trim()

  if (!normalizedValue) {
    throw createReloadParameterError(fieldName, '不能为空', nodeKey, value)
  }
  if (!/^\d+$/u.test(normalizedValue)) {
    throw createReloadParameterError(fieldName, '必须是 uint64 数字', nodeKey, value)
  }

  const parsedValue = BigInt(normalizedValue)
  if (parsedValue > UINT64_MAX) {
    throw createReloadParameterError(fieldName, '超出 uint64 范围', nodeKey, value)
  }

  return parsedValue
}

/**
 * 将必填正整数 ID 解析为 uint64。
 *
 * @param value 页面或模型树传入的 ID 原文。
 * @param fieldName 出错字段名。
 * @param nodeKey 当前重载目标节点 key。
 * @returns 大于 0 的 uint64 bigint。
 */
function parsePositiveUint64Id(value: string, fieldName: string, nodeKey: string): bigint {
  const parsedValue = parseUint64Id(value, fieldName, nodeKey)

  if (parsedValue <= 0n) {
    throw createReloadParameterError(fieldName, '必须大于 0', nodeKey, value)
  }

  return parsedValue
}

/**
 * 将可选 uint64 字符串规范化为后端默认值语义。
 *
 * @param value 页面或模型树传入的可选 ID。
 * @param fieldName 出错字段名。
 * @param nodeKey 当前重载目标节点 key。
 * @returns 空白或缺失时返回 0n，否则返回 uint64 bigint。
 */
function parseOptionalUint64Id(value: string | undefined, fieldName: string, nodeKey: string): bigint {
  const normalizedValue = value?.trim() ?? ''

  return normalizedValue ? parseUint64Id(normalizedValue, fieldName, nodeKey) : 0n
}

/**
 * 将可选提交 ID 规范化为后端默认 HEAD 语义。
 *
 * @param value 页面或模型树传入的可选提交 ID。
 * @returns trim 后的提交 ID，缺失或空白时返回空字符串。
 */
function normalizeOptionalCommitId(value: string | undefined): string {
  return value?.trim() ?? ''
}

/**
 * 校验重新加载响应中必须存在 CAD 节点。
 *
 * @param response 后端节点树响应。
 * @param nodeKey 当前重新加载目标的模型树 key。
 * @returns 可继续映射为模型资产树的 CAD 节点。
 */
function assertReloadedNode(response: CadNodeTreeReloadResponse, nodeKey: string): CadNode {
  if (!response.node) {
    throw new Error(`重新加载 CAD 节点失败：响应缺少 node，nodeKey=${nodeKey}`)
  }

  return response.node
}

/**
 * 创建 GetCadNodeTree 到项目模型节点重新加载能力的适配器。
 *
 * @param input 适配器依赖，包含实际通信函数。
 * @returns 节点重新加载 API 适配器。
 */
export function createCadNodeTreeReloadAdapter(input: CreateCadNodeTreeReloadAdapterInput): NodeReloadApiAdapter {
  /**
   * 执行一次 GetCadNodeTree，并保留响应级总归档包。
   *
   * @param request 节点定位信息。
   * @returns 可供加载策略判断完整场景模式的响应快照。
   */
  const reloadSnapshot = async (request: ReloadOpenProjectNodeInput): Promise<CadNodeLoadSnapshot> => {
    // repositoryId 是必填协议字段，必须先校验，避免 BigInt('') 这类输入被误转为 0n。
    const repositoryId = parsePositiveUint64Id(request.repositoryId, 'repositoryId', request.nodeKey)
    const response = await input.getCadNodeTree({
      repositoryId,
      branchId: parseOptionalUint64Id(request.branchId, 'branchId', request.nodeKey),
      commitId: normalizeOptionalCommitId(request.commitId),
    })
    const node = assertReloadedNode(response, request.nodeKey)
    const archiveFile = new CadArchiveFileSnapshotMapper().map(
      response.archiveFile,
      `GetCadNodeTree nodeKey=${request.nodeKey}`,
    )

    return {
      node,
      ...(archiveFile ? { archiveFile } : {}),
    }
  }

  return {
    reloadSnapshot,
  }
}
